import * as Comlink from 'comlink';
import * as polling from '../queues/polling';
import Contact from "../entities/contact";
import ENTITIES from './actions/index';
import User from "../entities/user";
import localforage from 'localforage';
import ls from '../lib/localStorage';
import store from './store';
import {EntityType} from "../@types/entity";
import * as idb from '../lib/idbd';
import {VERSION} from '../lib/idbd';
import {getEnvironment} from "../lib/get-environment";
import {precomputeContact, precomputeUser} from './hooks';
import {processQueue} from '../queues/processQueue';
import {cast} from "../entities/schemas";
import {RestoreResult} from "../workers/storage.worker";
import Alert from "../lib/alert";
import * as Actions from "./actions/miscActions";
import {tabSession} from '../Auth/session';
import type {SessionOperation} from '../Auth/tabSession';
import {upgradeAccountStore} from './upgradeAccountStore';

/**
 * Should we do excessive logging?
 */
let logging = ls.getBoolean(ls.RESTORE_LOGGING, false);

interface RestoreState {
    authenticatedFirmId: string;
    authenticatedUserId: string;

    [entity: string]: any;
}

/**
 * Config the storage
 *
 * @param {uuid} firmId
 * @param {uuid} userId
 */
export function config(firmId: string, userId: string) {
    // Set up localForage
    localforage.config({
        name: `Backups_${firmId}_${userId}`,
        driver: localforage.INDEXEDDB,
        description: `Redux backups for ${firmId}/${userId}`
    });
}

/**
 * Load the stored entities into redux.
 *
 * @param {object} state - state from misc
 */
export async function load(state: RestoreState, operation = tabSession.capture()) {
    operation.assertCurrent();
    if (state.authenticatedFirmId !== operation.identity.firmId || state.authenticatedUserId !== operation.identity.userId) {
        throw new Error('The backup owner does not match this tab.');
    }
    if (!state.authenticatedFirmId || !state.authenticatedUserId) {
        console.warn('⮂ restore: load called without firm and user Ids', state);
        return;
    }

    _loadDefaults();

    // OWA-525: RESTORED only when the backups were really loaded. A failed load keeps a failed
    // status (after its retries), so code waiting for RESTORED never acts on empty or old lists.
    let loaded = false;
    try {
        loaded = await _loadBackups(state, operation);
        operation.assertCurrent();
        if (logging) console.debug(`%c⮂ load: ${loaded ? '✔ Backups loaded' : '✘ Backups not loaded'}`, `color: ${loaded ? 'green' : 'maroon'}`);
    }
    catch (err: any) {
        operation.assertCurrent();
        console.error(`%c⮂ load: ✘ Backups not loaded. Error: ${err.message}`, 'color: maroon', err);
    }
    operation.assertCurrent();
    if (!loaded) reportRestoreFailed();

    // Clear out pre-computed contacts and users
    // TODO: We do this as needed now, is this required or even wanted?
    // store.getState().contacts.forEach((c: Contact) => precomputeContact(c));
    store.getState().users.forEach((u: User) => precomputeUser(u));
}

function _loadDefaults() {
    let logLevel = '';
    switch (getEnvironment()) {
        case 'P':
            logLevel = 'info';
            break;
        case 'DM':
        case 'T':
        case 'B':
            logLevel = 'log';
            break;
        default:
            logLevel = 'debug';
    }

    ls.setString(ls.LOGGING_LOG_LEVEL, logLevel); // Logging level, anything below this threshold is thrown away
}

function isOldEntity(value: unknown): value is EntityType {
    return typeof value === 'object' && value !== null && Object.hasOwn(value, '_addedOn');
}

export async function upgradeBackups(operation: SessionOperation) {
    await upgradeAccountStore('Backups', operation, (key, value) => {
        const single = ENTITIES.find(entity => entity.plural === key)?.name;
        if (!single || !Array.isArray(value) || !value.some(isOldEntity)) return value;
        return value.map(entity => isOldEntity(entity) ? cast<EntityType>(single, entity) : entity);
    });
}

export async function upgradeAutosave(operation: SessionOperation) {
    await upgradeAccountStore('Autosave', operation, (key, value) => {
        const single = key.split(' ')[0];
        if (!isOldEntity(value) || !ENTITIES.some(entity => entity.name === single)) return value;
        return cast(single, value);
    });
}

/**
 * Migrate the pre-version-35 unscoped 'Queues' database into the firm/user-
 * scoped queue database.
 *
 * OWA-786 (CU-5-C, CXR-9-002) rebuilt this as validate-prove-retire. The old
 * shape read the old database through the CURRENT schema (stores dropped from
 * the version chain were invisible), only console.error'd per-item copy
 * failures, and then unconditionally deleteDatabase'd the old queue — and its
 * "nothing to do" early-out was dead code (getOldDb never returns None), so
 * the whole cycle ran on every startup. Queue rows are durable user intent;
 * only rows PROVEN copied are retired, row by row, and every failure path
 * leaves everything in place for the next startup to retry. The database
 * itself is never deleted here: a deleteDatabase request pends unbounded
 * while another tab holds the old database open and then fires later,
 * destroying rows written after the proof.
 *
 * Exported only so it can be unit-tested; production calls it from
 * handleUpgrades.
 */
export async function upgradeQueues(firmId: string, userId: string, operation = tabSession.capture()) {
    operation.assertCurrent();
    if (operation.identity.firmId !== firmId || operation.identity.userId !== userId) {
        throw new Error('The queue migration owner does not match this tab.');
    }
    // Real "nothing to do" check: inventory the raw object stores (visible
    // even for stores the current schema dropped). Ok(null) = no old database,
    // which is the steady state after a completed migration.
    const inventoryResult = await idb.oldQueueInventory();
    operation.assertCurrent();
    if (inventoryResult.isErr()) {
        console.error('Unable to inventory the old Queues database', inventoryResult.unwrapErr());
        Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade (0).  Please contact Support for assistance. Error was ${inventoryResult.unwrapErr().message}`)
        return;
    }
    if (inventoryResult.unwrap() === null) return;
    const initialInventory = inventoryResult.unwrap()!;

    if (logging) console.debug(`%c⮂ restore: converting queues from version (${VERSION}) < 35`, "background: orange; color: white");

    const openResult = idb.open(firmId, userId);
    if (openResult.isErr()) {
        // If an error, we couldn't open the old database. This should not happen, but if it does, we need to let the user know.
        console.error('Unable to open Queues database', openResult.unwrapErr());
        Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade (1).  Please contact Support for assistance. Error was ${openResult.unwrapErr().message}`)
        return;
    }

    const allResult = await idb.readOwnedOldQueueRows(firmId, userId, openResult.unwrap().tables.map(table => table.name));
    operation.assertCurrent();
    if (allResult.isErr()) {
        // Errors here could happen even if there is no data in the queues.  We should let the user know.
        console.error('Unable to getAll on the Queues database', allResult.unwrapErr());
        Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade.  Please contact Support for assistance. Error was ${allResult.unwrapErr().message}`)
        return;
    }

    // Unwrap the data and start processing it
    const all = allResult.unwrap();

    // Cycle through the stores, adding them to the new Queues_firm_user database
    const keys = Object.keys(all);
    for (let entity of keys) {
        const entities = all[entity];
        try {
            // Now write them back to the store
            const addResult = await idb.add(entity, entities);
            operation.assertCurrent();
            if (addResult.isErr()) {
                // A failed copy keeps the old database; deleting it here would
                // destroy the only remaining record of this queued work.
                console.error(addResult.unwrapErr());
                Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade (3).  Please contact Support for assistance. Error was ${addResult.unwrapErr().message}`)
                return;
            }
        }
        catch (err) {
            operation.assertCurrent();
            console.error(err);
            Alert.addSimpleError(`We were unable to upgrade your uncommitted entries during an upgrade (3).  Please contact Support for assistance. Error was ${err.message}`)
            return;
        }
    }

    // Prove the copy row by row: every migrated operation must be readable in
    // the new database under its exact key before the old database may die.
    for (let entity of keys) {
        for (const row of all[entity]) {
            const copied = await idb.get(entity, row.addedOn);
            operation.assertCurrent();
            if (copied.isErr() || !copied.unwrap()) {
                console.error(`⮂ restore: migrated ${entity} row ${row.addedOn} is not readable in the new queue database — keeping the old database`);
                Alert.addSimpleError(`We were unable to verify your uncommitted entries after an upgrade (4).  Please contact Support for assistance.`)
                return;
            }
        }
    }

    // Retire from the old database exactly the rows proven copied — through
    // ordinary row transactions, never indexedDB.deleteDatabase (OWA-786,
    // CXR-9-002): a deleteDatabase request pends unbounded while another tab
    // holds the old database open, then fires when that tab closes and
    // destroys rows it wrote AFTER the proof. Row-level retirement leaves
    // mid-migration rows, and rows in stores the current schema cannot see,
    // untouched for a later startup; the empty database shell remains, and
    // its final removal belongs to the cross-tab cycle owner (OWA-787), which
    // can exclude writers for the whole interval. A failed retirement is
    // fail-soft: the copy already succeeded and was proven, and an un-retired
    // row re-copies idempotently on a later startup.
    for (let entity of keys) {
        // Pass the full proved row images (OWA-786, CXR-10-002): retirement
        // deletes a key only while its row still matches what was proven
        // copied, so a same-key rewrite by another tab survives.
        const retired = await idb.deleteOldQueueRows(entity, all[entity]);
        operation.assertCurrent();
        if (retired.isErr()) {
            console.warn(`⮂ restore: could not retire ${all[entity].length} migrated ${entity} row(s) from the old queue database; they will re-copy on a later startup. ${retired.unwrapErr().message}`);
        }
        else if (retired.unwrap() < all[entity].length) {
            // Shortfall is the designed survivor case (rewritten or already
            // gone), surfaced so a persistent gap is visible in logs.
            console.warn(`⮂ restore: retired ${retired.unwrap()} of ${all[entity].length} migrated ${entity} row(s); the remainder were rewritten or already gone and will reconcile on a later startup`);
        }
    }

    // Telemetry: rows in stores the current schema cannot read will never
    // migrate through this path; surface them rather than silently stranding.
    for (const [storeName, count] of initialInventory) {
        if (count > (all[storeName]?.length ?? 0)) {
            console.warn(`⮂ restore: old Queues store "${storeName}" has rows outside this verified account or current schema; they were left in place`);
            Alert.addSimpleError('Some older queued changes could not be assigned to this firm and user. They remain on this device. Sign in to their original account or contact Support for recovery.');
        }
    }

    // NOTE: do NOT idb.close() here. The cached `_db` at this point is the
    // firm-user-named Dexie instance opened above with full schema declared
    // and the IDB database created on disk. If we close, getDB() resets
    // `_db = None` and the next idb.add() (e.g. saveTask saga) creates a
    // FRESH Dexie wrapper with no schema declarations — Dexie 4 throws
    // NoSuchDatabaseError on the first table operation because schema
    // versions weren't declared on the new instance.
}

async function handleUpgrades(operation: SessionOperation) {
    await upgradeBackups(operation);
    operation.assertCurrent();
    await upgradeAutosave(operation);
    operation.assertCurrent();
    if (VERSION > 35) await upgradeQueues(operation.identity.firmId, operation.identity.userId, operation);
}

/**
 * Restore a single entity from IndexedDB backups into Redux.
 */
async function restoreEntity(entity: typeof ENTITIES[number], firmId: string, userId: string, operation: SessionOperation): Promise<RestoreResult> {
    // Async, so a missing storage worker is a rejected read (a failure), not a throw past the retries
    operation.assertCurrent();
    function fetchSuccess(entities: EntityType[]) {
        operation.assertCurrent();
        store.dispatch(entity.fetchSuccess(entities, false, 0));
    }

    function fetchFailure(err: Error) {
        operation.assertCurrent();
        store.dispatch(entity.fetchFailure(err));
    }

    return window.storageWorker.restore(
        firmId,
        userId,
        entity.name,
        entity.plural,
        Comlink.proxy(fetchSuccess),
        Comlink.proxy(fetchFailure)
    );
}

/**
 * Log restore errors from settled promises.
 */
function logRestoreErrors(settled: PromiseSettledResult<RestoreResult>[]) {
    for (const pr of settled) {
        if (pr.status === 'rejected') {
            console.error(`%c⮂ restore: Error restoring. Reason: %s`, "background: maroon; color: white;", pr.reason);
        }
        else if (pr.status === 'fulfilled') {
            if (pr.value.error) {
                if (!pr.value.error.startsWith(`No storage found for`))
                    console.error(`⮂ restore: Error restoring ${pr.value.entityName}. Reason: %s`, pr.value.error);
            }
        }
    }
}

/**
 * OWA-525: how long to wait before each retry of a failed backup read. The retries end well
 * inside the 10 seconds the queue's fetch waits for the restore (processQueue.ts), so a retried
 * backup does not arrive after the server's newer rows.
 */
export const RESTORE_RETRY_DELAYS_MS: readonly number[] = [500, 1_500, 3_000];

/** A read that failed: a rejection, or an error other than "there is no backup yet". */
function isFailedRead(result: PromiseSettledResult<RestoreResult>): boolean {
    if (result.status === 'rejected') return true;
    const error = result.value?.error;
    return !!error && !error.startsWith('No storage found for');
}

/** Read the given entities' backups once. Returns the entities whose read failed. */
async function restoreOnce(entities: typeof ENTITIES, firmId: string, userId: string, operation: SessionOperation): Promise<typeof ENTITIES> {
    const settled = await Promise.allSettled(entities.map(entity => restoreEntity(entity, firmId, userId, operation)));
    operation.assertCurrent();
    logRestoreErrors(settled);
    return entities.filter((_entity, i) => isFailedRead(settled[i]));
}

/**
 * Read the failed backups again after each delay, until all are read or the delays run out.
 * Returns the entities that still failed (empty when all were read).
 */
async function retryFailedReads(failed: typeof ENTITIES, firmId: string, userId: string, operation: SessionOperation): Promise<typeof ENTITIES> {
    let pending = failed;
    for (let retry = 0; pending.length > 0 && retry < RESTORE_RETRY_DELAYS_MS.length; retry++) {
        console.warn(`⮂ restore: retrying the backups of ${pending.map(e => e.name).join(', ')} (retry ${retry + 1} of ${RESTORE_RETRY_DELAYS_MS.length})`);
        await new Promise(resolve => setTimeout(resolve, RESTORE_RETRY_DELAYS_MS[retry]));
        operation.assertCurrent();
        pending = await restoreOnce(pending, firmId, userId, operation);
    }
    return pending;
}

/**
 * OWA-525: the backups could not be loaded. The status says so (the footer shows it), the user
 * is told once, and a full fetch is asked for: the store's lists may be empty, and a partial
 * fetch would only add the changes since the last fetch to them. Once that full fetch has
 * processed every entity, the queue reports RESTORED (processQueue fetchFromServer).
 */
export function reportRestoreFailed() {
    store.dispatch(Actions.updateLoading(false));
    store.dispatch(Actions.setRestoreStatus(Actions.RESTORE_FAILED));
    Alert.addSimpleError('Your saved data could not be loaded from this browser. It is being loaded from the server instead. If something looks missing, reload the page.', 15_000);
    try {
        polling.doFullRefresh('restore failed');
    }
    catch (err) {
        console.error('⮂ restore: could not ask for a full fetch after the failed restore', err);
    }
}

/**
 * Load the backups into the store. Returns true when every backup was read (a missing backup is
 * not a failure); RESTORED is reported only then.
 */
async function _loadBackups(state: RestoreState, operation: SessionOperation): Promise<boolean> {
    if (logging) console.debug(`⮂ restore: restoring backups for firm ${state.authenticatedFirmId} user ${state.authenticatedUserId}`);

    const start = Date.now();
    const firmId = state.authenticatedFirmId;
    const userId = state.authenticatedUserId;

    if (logging) console.debug('⮂ restore: started restore from backups');

    // Convert legacy values atomically in their original account store. Never drop
    // a database that can contain unsaved work or be open in another tab.
    try {
        await handleUpgrades(operation);
    }
    catch (err) {
        operation.assertCurrent();
        console.error('⮂ restore: handleUpgrades failed, continuing with restore', err);
        Alert.addSimpleError('Some saved work could not be upgraded. The original data remains on this device. Please contact Support if saved entries are missing.');
    }

    if (logging) console.debug(`⮂ restore: database version ${VERSION}`);
    operation.assertCurrent();

    // Phase 1: Restore firm and user first so getCurrentUser()/getCurrentFirm() resolve immediately,
    // allowing Home.tsx to render the Dashboard instead of showing a blank screen.
    const priorityNames = ['firm', 'user'];
    const priorityEntities = ENTITIES.filter(e => priorityNames.includes(e.name));
    const remainingEntities = ENTITIES.filter(e => !priorityNames.includes(e.name));

    if (logging) console.debug(`⮂ restore: Phase 1 — restoring ${priorityEntities.map(e => e.name).join(', ')}`);

    const priorityFailed = await restoreOnce(priorityEntities, firmId, userId, operation);

    if (logging) console.debug(`%c⮂ restore: Phase 1 ✔ firm+user restored in ${Date.now() - start}ms`, 'color: green');

    // Phase 2: Restore all remaining entities in parallel.
    if (logging) console.debug(`⮂ restore: Phase 2 — restoring ${remainingEntities.length} remaining entities`);

    const remainingFailed = await restoreOnce(remainingEntities, firmId, userId, operation);

    // OWA-525: retry the failed reads together, so the retries take one bounded interval
    const failed = await retryFailedReads([...priorityFailed, ...remainingFailed], firmId, userId, operation);
    if (failed.length > 0) {
        console.error(`⮂ restore: the backups of ${failed.map(e => e.name).join(', ')} could not be loaded`);
        return false;
    }

    if (logging) console.debug(`%c⮂ ✔ All entities restored in ${Date.now() - start}ms`, 'color: green');

    // Mark restore as complete BEFORE processQueue/polling — both of those wait for RESTORED,
    // so dispatching it here breaks the circular dependency.
    store.dispatch(Actions.updateLoading(false));
    store.dispatch(Actions.setRestoreStatus(Actions.RESTORED));

    // Now flush the queue and start polling
    try {
        await processQueue();
        operation.assertCurrent();
        await polling.start('restore');
    }
    catch (err) {
        console.error('⮂ restore failed to start processing the queue', err);
    }
    return true;
}
